import { useState } from 'react';
import { Link } from 'react-router-dom';
import { addDoc, collection, deleteDoc, doc, setDoc, updateDoc } from 'firebase/firestore';
import { Eye, EyeOff, Pencil, Plus, TicketPercent, Trash2 } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAdminCollection } from './hooks';
import { EmptyState, Field, Modal } from './adminUi';
import { VEHICLE_CLASSES, type Addon, type Package, type PromoCode } from '../../lib/types';
import { money, todayIso } from '../../lib/format';

const emptyPackage: Omit<Package, 'id'> = {
  name: '',
  description: '',
  category: 'full',
  features: [],
  prices: { car: 0, midsize: 0, large: 0, oversized: 0 },
  durationHours: 2,
  popular: false,
  active: true,
  order: 99,
};

const emptyAddon: Omit<Addon, 'id'> = {
  name: '',
  description: '',
  price: 0,
  fromPrice: false,
  active: true,
  order: 99,
};

const emptyPromo: Omit<PromoCode, 'id'> & { id: string } = {
  id: '',
  type: 'percent',
  value: 10,
  active: true,
  expires: '',
  note: '',
};

export default function PackagesAdmin() {
  const [tab, setTab] = useState<'packages' | 'addons' | 'promos'>('packages');
  const packages = useAdminCollection<Package>('packages');
  const addons = useAdminCollection<Addon>('addons');
  const promos = useAdminCollection<PromoCode>('promoCodes');
  const [editingPkg, setEditingPkg] = useState<Partial<Package> | null>(null);
  const [editingAddon, setEditingAddon] = useState<Partial<Addon> | null>(null);
  const [editingPromo, setEditingPromo] = useState<(Partial<PromoCode> & { isNew?: boolean }) | null>(null);
  const [saving, setSaving] = useState(false);

  const sortedPackages = [...packages.items].sort((a, b) => a.order - b.order);
  const sortedAddons = [...addons.items].sort((a, b) => a.order - b.order);

  const savePkg = async () => {
    if (!editingPkg?.name) return;
    setSaving(true);
    const { id, ...data } = { ...emptyPackage, ...editingPkg };
    try {
      if (id) await updateDoc(doc(db(), 'packages', id), data as any);
      else await addDoc(collection(db(), 'packages'), data);
      setEditingPkg(null);
    } finally {
      setSaving(false);
    }
  };

  const saveAddon = async () => {
    if (!editingAddon?.name) return;
    setSaving(true);
    const { id, ...data } = { ...emptyAddon, ...editingAddon };
    try {
      if (id) await updateDoc(doc(db(), 'addons', id), data as any);
      else await addDoc(collection(db(), 'addons'), data);
      setEditingAddon(null);
    } finally {
      setSaving(false);
    }
  };

  const removeDoc = async (coll: string, id: string, name: string) => {
    if (confirm(`Delete "${name}"? This can't be undone.`)) await deleteDoc(doc(db(), coll, id));
  };

  const toggleActive = (coll: string, id: string, active: boolean) =>
    updateDoc(doc(db(), coll, id), { active: !active });

  const dbEmpty = !packages.loading && packages.items.length === 0;

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Packages & Add-ons</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Everything here updates the live site instantly — prices, features, ordering, visibility.
          </p>
        </div>
        <button
          className="btn-primary !py-2.5 text-sm"
          onClick={() =>
            tab === 'packages'
              ? setEditingPkg({ ...emptyPackage })
              : tab === 'addons'
                ? setEditingAddon({ ...emptyAddon })
                : setEditingPromo({ ...emptyPromo, isNew: true })
          }
        >
          <Plus size={16} /> New {tab === 'packages' ? 'Package' : tab === 'addons' ? 'Add-on' : 'Promo Code'}
        </button>
      </div>

      {dbEmpty && (
        <div className="card mt-6 border-accent-500/40 p-5 text-sm text-zinc-300">
          The database has no packages yet — the public site is showing built-in starter content.{' '}
          <Link to="/admin" className="font-semibold text-accent-400 hover:text-accent-300">
            Publish starter content from the Dashboard
          </Link>{' '}
          to load it here for editing, or create packages from scratch.
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        {(['packages', 'addons', 'promos'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-full px-5 py-2 text-sm font-semibold capitalize transition-all ${
              tab === t ? 'bg-gradient-to-r from-accent-600 to-sky-500 text-white' : 'glass text-zinc-400 hover:text-white'
            }`}
          >
            {t === 'packages'
              ? `Packages (${packages.items.length})`
              : t === 'addons'
                ? `Add-ons (${addons.items.length})`
                : `Promo Codes (${promos.items.length})`}
          </button>
        ))}
      </div>

      {tab === 'packages' &&
        (sortedPackages.length === 0 ? (
          <EmptyState text="No packages in the database yet." />
        ) : (
          <div className="mt-6 space-y-3">
            {sortedPackages.map((p) => (
              <div key={p.id} className={`card flex flex-wrap items-center gap-4 p-4 sm:px-5 ${!p.active ? 'opacity-50' : ''}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-display font-semibold text-white">
                    {p.name}
                    {p.popular && (
                      <span className="ml-2 rounded-full bg-accent-500/20 px-2 py-0.5 text-[10px] font-bold uppercase text-accent-300">Popular</span>
                    )}
                    {!p.active && (
                      <span className="ml-2 rounded-full bg-zinc-500/20 px-2 py-0.5 text-[10px] font-bold uppercase text-zinc-400">Hidden</span>
                    )}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-zinc-500">
                    {p.features.length} features · ~{p.durationHours}h · order {p.order}
                  </p>
                </div>
                <div className="hidden gap-3 text-sm md:flex">
                  {VEHICLE_CLASSES.map((vc) => (
                    <span key={vc.id} className="text-zinc-300" title={vc.label}>
                      {money(p.prices[vc.id] ?? 0)}
                    </span>
                  ))}
                </div>
                <div className="flex gap-1.5">
                  <button className="glass rounded-lg p-2 text-zinc-300 hover:text-white" title={p.active ? 'Hide from site' : 'Show on site'} onClick={() => toggleActive('packages', p.id, p.active)}>
                    {p.active ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                  <button className="glass rounded-lg p-2 text-zinc-300 hover:text-accent-300" title="Edit" onClick={() => setEditingPkg(p)}>
                    <Pencil size={16} />
                  </button>
                  <button className="glass rounded-lg p-2 text-zinc-300 hover:text-red-400" title="Delete" onClick={() => removeDoc('packages', p.id, p.name)}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ))}

      {tab === 'addons' &&
        (sortedAddons.length === 0 ? (
          <EmptyState text="No add-ons in the database yet." />
        ) : (
          <div className="mt-6 space-y-3">
            {sortedAddons.map((a) => (
              <div key={a.id} className={`card flex flex-wrap items-center gap-4 p-4 sm:px-5 ${!a.active ? 'opacity-50' : ''}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-display font-semibold text-white">{a.name}</p>
                  <p className="mt-0.5 truncate text-xs text-zinc-500">{a.description}</p>
                </div>
                <span className="font-display font-bold text-accent-300">
                  {a.fromPrice && <span className="text-xs font-medium text-zinc-500">from </span>}
                  {money(a.price)}
                </span>
                <div className="flex gap-1.5">
                  <button className="glass rounded-lg p-2 text-zinc-300 hover:text-white" title={a.active ? 'Hide' : 'Show'} onClick={() => toggleActive('addons', a.id, a.active)}>
                    {a.active ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                  <button className="glass rounded-lg p-2 text-zinc-300 hover:text-accent-300" title="Edit" onClick={() => setEditingAddon(a)}>
                    <Pencil size={16} />
                  </button>
                  <button className="glass rounded-lg p-2 text-zinc-300 hover:text-red-400" title="Delete" onClick={() => removeDoc('addons', a.id, a.name)}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ))}

      {tab === 'promos' &&
        (promos.items.length === 0 ? (
          <EmptyState text='No promo codes yet — create one like "GRANDOPEN15" and share it in your marketing.' />
        ) : (
          <div className="mt-6 space-y-3">
            {[...promos.items]
              .sort((a, b) => a.id.localeCompare(b.id))
              .map((p) => {
                const expired = p.expires && p.expires < todayIso();
                return (
                  <div key={p.id} className={`card flex flex-wrap items-center gap-4 p-4 sm:px-5 ${!p.active || expired ? 'opacity-50' : ''}`}>
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.06] text-accent-400">
                      <TicketPercent size={18} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-display font-semibold tracking-wider text-white">{p.id}</p>
                      <p className="mt-0.5 text-xs text-zinc-500">
                        {p.type === 'percent' ? `${p.value}% off` : `${money(p.value)} off`}
                        {p.expires ? ` · expires ${p.expires}` : ' · no expiry'}
                        {expired ? ' · EXPIRED' : ''}
                        {p.note ? ` · ${p.note}` : ''}
                      </p>
                    </div>
                    <div className="flex gap-1.5">
                      <button
                        className="glass rounded-lg p-2 text-zinc-300 hover:text-white"
                        title={p.active ? 'Deactivate' : 'Activate'}
                        onClick={() => updateDoc(doc(db(), 'promoCodes', p.id), { active: !p.active })}
                      >
                        {p.active ? <Eye size={16} /> : <EyeOff size={16} />}
                      </button>
                      <button className="glass rounded-lg p-2 text-zinc-300 hover:text-accent-300" title="Edit" onClick={() => setEditingPromo(p)}>
                        <Pencil size={16} />
                      </button>
                      <button
                        className="glass rounded-lg p-2 text-zinc-300 hover:text-red-400"
                        title="Delete"
                        onClick={async () => {
                          if (confirm(`Delete code ${p.id}?`)) await deleteDoc(doc(db(), 'promoCodes', p.id));
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        ))}

      {/* Promo editor */}
      {editingPromo && (
        <Modal title={editingPromo.isNew ? 'New Promo Code' : `Edit ${editingPromo.id}`} onClose={() => setEditingPromo(null)}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Code (what customers type)">
              <input
                className="input uppercase"
                value={editingPromo.id ?? ''}
                disabled={!editingPromo.isNew}
                onChange={(e) => setEditingPromo({ ...editingPromo, id: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                placeholder="GRANDOPEN15"
                maxLength={20}
              />
            </Field>
            <Field label="Discount type">
              <select
                className="input"
                value={editingPromo.type ?? 'percent'}
                onChange={(e) => setEditingPromo({ ...editingPromo, type: e.target.value as PromoCode['type'] })}
              >
                <option value="percent">Percent off</option>
                <option value="fixed">Dollars off (CAD)</option>
              </select>
            </Field>
            <Field label={editingPromo.type === 'fixed' ? 'Amount off ($)' : 'Percent off (%)'}>
              <input
                className="input"
                type="number"
                min={1}
                max={editingPromo.type === 'fixed' ? 10000 : 100}
                value={editingPromo.value ?? 10}
                onChange={(e) => setEditingPromo({ ...editingPromo, value: Number(e.target.value) })}
              />
            </Field>
            <Field label="Expires (blank = never)">
              <input
                className="input [color-scheme:dark]"
                type="date"
                value={editingPromo.expires ?? ''}
                onChange={(e) => setEditingPromo({ ...editingPromo, expires: e.target.value })}
              />
            </Field>
            <Field label="Note (only you see this)" span2>
              <input
                className="input"
                value={editingPromo.note ?? ''}
                onChange={(e) => setEditingPromo({ ...editingPromo, note: e.target.value })}
                placeholder="Instagram launch promo"
              />
            </Field>
            <label className="flex items-center gap-2 text-sm text-zinc-300 sm:col-span-2">
              <input
                type="checkbox"
                className="h-4 w-4 accent-sky-500"
                checked={editingPromo.active ?? true}
                onChange={(e) => setEditingPromo({ ...editingPromo, active: e.target.checked })}
              />
              Active
            </label>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button className="btn-ghost !py-2.5 text-sm" onClick={() => setEditingPromo(null)}>Cancel</button>
            <button
              className="btn-primary !py-2.5 text-sm"
              disabled={saving || !editingPromo.id || (editingPromo.id?.length ?? 0) < 3}
              onClick={async () => {
                setSaving(true);
                try {
                  const { id, isNew, ...data } = { ...emptyPromo, ...editingPromo };
                  await setDoc(doc(db(), 'promoCodes', id!), data);
                  setEditingPromo(null);
                } finally {
                  setSaving(false);
                }
              }}
            >
              {saving ? 'Saving…' : 'Save Code'}
            </button>
          </div>
        </Modal>
      )}

      {/* Package editor */}
      {editingPkg && (
        <Modal title={editingPkg.id ? `Edit ${editingPkg.name}` : 'New Package'} onClose={() => setEditingPkg(null)}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" span2>
              <input className="input" value={editingPkg.name ?? ''} onChange={(e) => setEditingPkg({ ...editingPkg, name: e.target.value })} />
            </Field>
            <Field label="Description" span2>
              <textarea className="input min-h-20" value={editingPkg.description ?? ''} onChange={(e) => setEditingPkg({ ...editingPkg, description: e.target.value })} />
            </Field>
            <Field label="Category">
              <select className="input" value={editingPkg.category ?? 'full'} onChange={(e) => setEditingPkg({ ...editingPkg, category: e.target.value as Package['category'] })}>
                <option value="full">Interior + Exterior</option>
                <option value="interior">Interior</option>
                <option value="exterior">Exterior</option>
              </select>
            </Field>
            <Field label="Duration (hours)">
              <input className="input" type="number" min={0.5} step={0.5} value={editingPkg.durationHours ?? 2} onChange={(e) => setEditingPkg({ ...editingPkg, durationHours: Number(e.target.value) })} />
            </Field>
            {VEHICLE_CLASSES.map((vc) => (
              <Field key={vc.id} label={`Price — ${vc.label}`}>
                <input
                  className="input"
                  type="number"
                  min={0}
                  value={editingPkg.prices?.[vc.id] ?? 0}
                  onChange={(e) =>
                    setEditingPkg({
                      ...editingPkg,
                      prices: { ...emptyPackage.prices, ...editingPkg.prices, [vc.id]: Number(e.target.value) },
                    })
                  }
                />
              </Field>
            ))}
            <Field label="Features (one per line)" span2>
              <textarea
                className="input min-h-32"
                value={(editingPkg.features ?? []).join('\n')}
                onChange={(e) => setEditingPkg({ ...editingPkg, features: e.target.value.split('\n').filter((l) => l.trim() !== '') })}
              />
            </Field>
            <Field label="Display order">
              <input className="input" type="number" value={editingPkg.order ?? 99} onChange={(e) => setEditingPkg({ ...editingPkg, order: Number(e.target.value) })} />
            </Field>
            <div className="flex items-end gap-5 pb-1">
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" className="h-4 w-4 accent-sky-500" checked={editingPkg.popular ?? false} onChange={(e) => setEditingPkg({ ...editingPkg, popular: e.target.checked })} />
                Most Popular badge
              </label>
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" className="h-4 w-4 accent-sky-500" checked={editingPkg.active ?? true} onChange={(e) => setEditingPkg({ ...editingPkg, active: e.target.checked })} />
                Visible on site
              </label>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button className="btn-ghost !py-2.5 text-sm" onClick={() => setEditingPkg(null)}>Cancel</button>
            <button className="btn-primary !py-2.5 text-sm" onClick={savePkg} disabled={saving || !editingPkg.name}>
              {saving ? 'Saving…' : 'Save Package'}
            </button>
          </div>
        </Modal>
      )}

      {/* Add-on editor */}
      {editingAddon && (
        <Modal title={editingAddon.id ? `Edit ${editingAddon.name}` : 'New Add-on'} onClose={() => setEditingAddon(null)}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" span2>
              <input className="input" value={editingAddon.name ?? ''} onChange={(e) => setEditingAddon({ ...editingAddon, name: e.target.value })} />
            </Field>
            <Field label="Description" span2>
              <textarea className="input min-h-20" value={editingAddon.description ?? ''} onChange={(e) => setEditingAddon({ ...editingAddon, description: e.target.value })} />
            </Field>
            <Field label="Price">
              <input className="input" type="number" min={0} value={editingAddon.price ?? 0} onChange={(e) => setEditingAddon({ ...editingAddon, price: Number(e.target.value) })} />
            </Field>
            <Field label="Display order">
              <input className="input" type="number" value={editingAddon.order ?? 99} onChange={(e) => setEditingAddon({ ...editingAddon, order: Number(e.target.value) })} />
            </Field>
            <div className="flex items-center gap-5 sm:col-span-2">
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" className="h-4 w-4 accent-sky-500" checked={editingAddon.fromPrice ?? false} onChange={(e) => setEditingAddon({ ...editingAddon, fromPrice: e.target.checked })} />
                Show as "from" price
              </label>
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" className="h-4 w-4 accent-sky-500" checked={editingAddon.active ?? true} onChange={(e) => setEditingAddon({ ...editingAddon, active: e.target.checked })} />
                Visible on site
              </label>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button className="btn-ghost !py-2.5 text-sm" onClick={() => setEditingAddon(null)}>Cancel</button>
            <button className="btn-primary !py-2.5 text-sm" onClick={saveAddon} disabled={saving || !editingAddon.name}>
              {saving ? 'Saving…' : 'Save Add-on'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
